import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { RevealText } from "../motion/Reveal";
import {
  HERO,
  HERO_VIDEO,
  HERO_VIDEO_MOBILE,
  HERO_VIDEO_MOBILE_WEBM,
  HERO_VIDEO_WEBM,
} from "../../data/content";

export const Hero = ({ onContact }) => {
  const ref = useRef(null);
  const [isMobileHero, setIsMobileHero] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const videoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const orb2Y = useSpring(useTransform(scrollYProgress, [0, 1], [0, -180]), {
    stiffness: 60,
    damping: 20,
  });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobileHero(media.matches);
    update();
    media.addEventListener("change", update);

    return () => media.removeEventListener("change", update);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-center"
    >
      {/* Full-screen background video */}
      <motion.div style={{ y: videoY }} className="absolute inset-0 -z-0 scale-110">
        <video
          key={isMobileHero ? "mobile-hero-video" : "desktop-hero-video"}
          data-testid="hero-video"
          className="h-full w-full object-cover object-[72%_center] md:object-center"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/hero-poster.webp"
        >
          <source
            src={isMobileHero ? HERO_VIDEO_MOBILE_WEBM : HERO_VIDEO_WEBM}
            type="video/webm"
          />
          <source src={isMobileHero ? HERO_VIDEO_MOBILE : HERO_VIDEO} type="video/mp4" />
        </video>
      </motion.div>

      {/* Cinematic overlays for legibility + warm brand tint */}
      <div className="pointer-events-none absolute inset-0 bg-black/40" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black via-black/45 to-black/10" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/50" />
      <motion.div
        style={{ y: orb2Y }}
        className="pointer-events-none absolute -top-32 -left-24 h-[520px] w-[520px] rounded-full blur-[150px] opacity-40"
      >
        <div className="h-full w-full rounded-full bg-[#ff4500]" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 pt-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          data-testid="hero-eyebrow"
          className="font-body text-xs md:text-sm uppercase tracking-[0.28em] text-neutral-400 mb-8"
        >
          {HERO.eyebrow}
        </motion.p>

        <RevealText
          lines={HERO.lines}
          delay={0.4}
          className="font-display font-extrabold text-white text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.92] tracking-tight"
        />

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            data-testid="hero-subtitle"
            className="font-body text-lg md:text-xl text-neutral-300 max-w-xl leading-relaxed font-light"
          >
            {HERO.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onContact}
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-white text-black font-body font-medium px-7 py-4 hover:bg-[#ff4500] hover:text-white transition-[transform,background-color,color,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-12px_rgba(255,69,0,0.55)]"
            >
              {HERO.primaryCta}
              <ArrowRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-neutral-500"
      >
        <span className="font-body text-[11px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};
